import { postSalesOrderResolve } from "../api/orderResolveApi";
import { buildSalesResolvePayloadFromSavedOrder } from "../builders/buildSalesResolvePayloadFromSavedOrder";

/**
 * After checkout payment is recorded as Paid: resolve with action complete,
 * then PATCH orders-update with version + order_tax_summary only.
 */
export async function finalizeSalesOrderTaxAfterPaid(params: {
  token: string;
  orderId: string;
  order: Record<string, unknown>;
}): Promise<void> {
  const orderId = String(params.orderId || "").trim();
  if (!orderId || !params.token) return;

  const existingSummary =
    typeof params.order.order_tax_summary === "object" && params.order.order_tax_summary
      ? (params.order.order_tax_summary as Record<string, unknown>)
      : {};

  let orderTaxSummary: Record<string, unknown> = {
    tax_inclusive: existingSummary.tax_inclusive ?? false,
    over_all_tax_amount: existingSummary.over_all_tax_amount ?? 0,
    over_all_tax_percentage: existingSummary.over_all_tax_percentage ?? 0,
    over_all_taxable_base: existingSummary.over_all_taxable_base ?? 0,
    tax_status: "complete",
  };

  try {
    const resolvePayload = buildSalesResolvePayloadFromSavedOrder(params.order);
    const resolved = await postSalesOrderResolve(params.token, {
      order_type: "order",
      action: "complete",
      payload: resolvePayload,
    });
    if (resolved?.order_tax_summary) {
      orderTaxSummary = {
        tax_inclusive: resolved.order_tax_summary.tax_inclusive ?? false,
        over_all_tax_amount: resolved.order_tax_summary.over_all_tax_amount ?? 0,
        over_all_tax_percentage: resolved.order_tax_summary.over_all_tax_percentage ?? 0,
        over_all_taxable_base: resolved.order_tax_summary.over_all_taxable_base ?? 0,
        tax_status: "complete",
      };
    }
  } catch {
    // Resolve failed — still PATCH tax_status complete using existing summary values.
  }

  const orderData =
    typeof (params.order as { data?: unknown }).data === "object" &&
    (params.order as { data?: unknown }).data
      ? ((params.order as { data: Record<string, unknown> }).data)
      : params.order;
  const version =
    params.order.version ??
    orderData.version ??
    (params.order as { __v?: unknown }).__v ??
    (orderData as { __v?: unknown }).__v ??
    null;

  const bodyPatch: Record<string, unknown> = {
    version,
    order_tax_summary: orderTaxSummary,
  };

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/orders-update/${orderId}?update_context=tax`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
      },
      body: JSON.stringify(bodyPatch),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(
      (errorData as { message?: string })?.message ||
        `Failed to finalize order tax after payment (${response.status})`
    );
  }
}

/** GET latest sales order snapshot for post-payment resolve. */
export async function fetchSalesOrderForPaidFinalize(
  token: string,
  orderId: string
): Promise<Record<string, unknown> | null> {
  const id = String(orderId || "").trim();
  if (!id || !token) return null;

  const response = await fetch(`${process.env.REACT_APP_API_URL}/orders/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) return null;
  const data = await response.json().catch(() => null);
  return data && typeof data === "object" ? (data as Record<string, unknown>) : null;
}
